import type { RevitOperationProgressSnapshot } from '../api/grpc/operationProgressClient';
import type { ConnectorFamilyTargetDisposition } from '../lib/hooks/parameterAddition/connectorFamilyTargetPolicy';
import type { ParameterAdditionResult } from '../lib/hooks/parameterAddition/models';
import type { ParameterBindingKind } from '../lib/revit/projectParameterMutationPlan';

export const CONNECTOR_LIVE_EVIDENCE_QUERY_PARAM = 'e2eLiveConnectorEvidence';

export interface ConnectorLiveEvidenceRequest {
  parameterNames: string[];
  categories: string[];
  requestedBindingKind: ParameterBindingKind;
  effectiveBindingKind: ParameterBindingKind;
  parameterTypeHints: Array<{ name: string; dataType: string }>;
  backendParameters: Array<{ name: string; dataType: string }>;
}

export interface ConnectorLiveEvidenceCall {
  request: ConnectorLiveEvidenceRequest;
  rawBackendResult?: unknown;
}

export interface ConnectorLiveEvidenceDispatch {
  destination: 'project' | 'family';
  // The binding kind exactly as sent at the gRPC boundary; family requests
  // may carry an empty string when a non-connector caller omitted it.
  bindingKind: ParameterBindingKind | string;
  categories: string[];
  expectedDocumentName: string | null;
  expectedDocumentPath: string | null;
  operationId: string | null;
}

/**
 * Why a Family mode did — or did not — dispatch its editable-family mutation.
 * A skipped family half is a legitimate outcome (nothing left to mutate), so
 * the live route evidence must carry the snapshot facts that produced the
 * decision; otherwise a missing dispatch is indistinguishable from a defect.
 */
export interface ConnectorLiveEvidenceFamilySelection {
  bindingKind: ParameterBindingKind | string;
  requestedCategories: string[];
  snapshotAvailable: boolean;
  snapshotFamilyCount: number;
  snapshotCategories: string[];
  matchingFamilyCount: number;
  compatibleFamilyCount: number;
  mutationTargetFamilyCount: number;
  disposition: ConnectorFamilyTargetDisposition;
}

export interface ConnectorLiveEvidenceSnapshot {
  calls: ConnectorLiveEvidenceCall[];
  dispatches: ConnectorLiveEvidenceDispatch[];
  familySelections: ConnectorLiveEvidenceFamilySelection[];
  normalizedProgress: RevitOperationProgressSnapshot[];
  normalizedFrontendResult?: ParameterAdditionResult;
}

declare global {
  interface Window {
    __PIPEEX_CONNECTOR_LIVE_EVIDENCE__?: ConnectorLiveEvidenceSnapshot;
  }
}

export function isConnectorLiveEvidenceEnabled(
  search: string,
  e2eBuildFlag: string | undefined,
): boolean {
  return e2eBuildFlag === '1' &&
    new URLSearchParams(search).get(CONNECTOR_LIVE_EVIDENCE_QUERY_PARAM) === '1';
}

function getEvidenceSnapshot(): ConnectorLiveEvidenceSnapshot | null {
  if (
    typeof window === 'undefined' ||
    !isConnectorLiveEvidenceEnabled(
      window.location.search,
      process.env.REACT_APP_PIPEEX_E2E_BYPASS,
    )
  ) {
    return null;
  }

  return window.__PIPEEX_CONNECTOR_LIVE_EVIDENCE__ ?? null;
}

export function resetConnectorLiveEvidence(): void {
  if (
    typeof window === 'undefined' ||
    !isConnectorLiveEvidenceEnabled(
      window.location.search,
      process.env.REACT_APP_PIPEEX_E2E_BYPASS,
    )
  ) {
    return;
  }

  window.__PIPEEX_CONNECTOR_LIVE_EVIDENCE__ = {
    calls: [],
    dispatches: [],
    familySelections: [],
    normalizedProgress: [],
  };
}

export function recordConnectorLiveEvidenceRequest(
  request: ConnectorLiveEvidenceRequest,
): number | null {
  const snapshot = getEvidenceSnapshot();
  if (!snapshot) {
    return null;
  }

  snapshot.calls.push({ request });
  return snapshot.calls.length - 1;
}

export function recordConnectorLiveEvidenceRawResult(
  callIndex: number | null,
  rawBackendResult: unknown,
): void {
  const snapshot = getEvidenceSnapshot();
  if (!snapshot || callIndex === null || !snapshot.calls[callIndex]) {
    return;
  }

  snapshot.calls[callIndex].rawBackendResult = rawBackendResult;
}

export function recordConnectorLiveEvidenceDispatch(
  dispatch: ConnectorLiveEvidenceDispatch,
): number | null {
  const snapshot = getEvidenceSnapshot();
  if (!snapshot) {
    return null;
  }

  snapshot.dispatches.push(dispatch);
  return snapshot.dispatches.length - 1;
}

export function recordConnectorLiveEvidenceDispatchOperationId(
  dispatchIndex: number | null,
  operationId: string,
): void {
  const snapshot = getEvidenceSnapshot();
  if (!snapshot || dispatchIndex === null || !snapshot.dispatches[dispatchIndex]) {
    return;
  }

  snapshot.dispatches[dispatchIndex].operationId = operationId;
}

export function recordConnectorLiveEvidenceFamilySelection(
  selection: ConnectorLiveEvidenceFamilySelection,
): void {
  getEvidenceSnapshot()?.familySelections.push(selection);
}

export function recordConnectorLiveEvidenceProgress(
  progress: RevitOperationProgressSnapshot,
): void {
  getEvidenceSnapshot()?.normalizedProgress.push(progress);
}

export function recordConnectorLiveEvidenceFrontendResult(
  result: ParameterAdditionResult,
): void {
  const snapshot = getEvidenceSnapshot();
  if (snapshot) {
    snapshot.normalizedFrontendResult = result;
  }
}
